/**
 * purchaseIntent — preserve the customer's selected plan + billing
 * interval across sign-in, registration, and email verification.
 *
 * The public purchase buttons on /n2/plans are the entry point. When a
 * signed-out visitor clicks one, we:
 *
 *   1. Stash `{planKey, billing, ts}` in sessionStorage.
 *   2. Redirect to /login with `?redirect=/upgrade&plan=…&billing=…`.
 *   3. On successful Login / Register / VerifyEmail, the auth screens
 *      call `consumeIntent()` which returns the stashed intent (and
 *      clears it) so they can navigate straight to
 *      `/upgrade?plan=…&billing=…` and drop the visitor at the correct
 *      checkout.
 *
 * The URL params are the primary carrier — sessionStorage is a
 * defensive backup for flows that lose the query string (email
 * verify link, third-party redirect). Neither is required for the
 * checkout to work.
 *
 * ENV flag `REACT_APP_PUBLIC_PURCHASE_ENABLED` — when unset or `false`
 * the public purchase buttons remain the current "See subscription
 * details" links, so this module and its consumers are dormant. Only
 * the operator flipping the flag activates the flow, and only after
 * the Stripe test-mode verifications listed in
 * docs/wiki/decisions/2026-09-14-notches-plan-catalog-precheckout.md
 * are complete.
 */

export type IntentBilling = 'monthly' | 'yearly';

export interface PurchaseIntent {
  planKey: string;
  billing: IntentBilling;
  ts: number;
}

const STORAGE_KEY = 'notches.purchaseIntent.v1';
const MAX_AGE_MS = 30 * 60 * 1000; // 30 min — the auth funnel is short

export function isPublicPurchaseEnabled(): boolean {
  const v = String(process.env.REACT_APP_PUBLIC_PURCHASE_ENABLED || '').toLowerCase();
  return v === 'true' || v === '1' || v === 'yes';
}

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function saveIntent(intent: Omit<PurchaseIntent, 'ts'>): void {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(STORAGE_KEY, JSON.stringify({ ...intent, ts: Date.now() }));
  } catch {
    /* private mode, quota, blocked — non-fatal */
  }
}

export function loadIntent(): PurchaseIntent | null {
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PurchaseIntent;
    if (!parsed || typeof parsed.planKey !== 'string' || (parsed.billing !== 'monthly' && parsed.billing !== 'yearly')) {
      return null;
    }
    if (typeof parsed.ts !== 'number' || Date.now() - parsed.ts > MAX_AGE_MS) {
      clearIntent();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearIntent(): void {
  const s = storage();
  if (!s) return;
  try { s.removeItem(STORAGE_KEY); } catch { /* non-fatal */ }
}

/**
 * The canonical URL for the focused "Continue to secure checkout"
 * confirmation screen. This is where a customer lands after selecting
 * a plan on /n2/plans and completing the auth funnel — going directly
 * to their plan's Stripe checkout, not the full /upgrade list.
 *
 * `/upgrade` remains the canonical destination for customers visiting
 * it directly (e.g., from Dashboard or from a legacy email link).
 */
export function checkoutContinueUrl(intent: { planKey: string; billing: IntentBilling }): string {
  const params = new URLSearchParams({ plan: intent.planKey, billing: intent.billing });
  return `/subscription/continue?${params.toString()}`;
}

/**
 * @deprecated Retained only for backwards compatibility with any older
 * callers that still route through /upgrade. New code uses
 * checkoutContinueUrl.
 */
export function upgradeUrlWithIntent(intent: { planKey: string; billing: IntentBilling }): string {
  const params = new URLSearchParams({ plan: intent.planKey, billing: intent.billing });
  return `/upgrade?${params.toString()}`;
}

/**
 * Consume + clear. Returns the intent if any, then removes it so a
 * subsequent auth-flow visit doesn't re-fire the redirect.
 */
export function consumeIntent(): PurchaseIntent | null {
  const intent = loadIntent();
  if (intent) clearIntent();
  return intent;
}
